import React, { useContext, useEffect, useState } from "react";
import BreadCrumbs from "src/components/BreadCrumbs";
import { Container, EmptyContainer, NoTicketsMessage, PaginationContainer } from "src/components/ListScreen";
import { HorizontalTabs } from "src/components/Tabs/HorizontalTabs";
import { Content, ContentFrame, TopBar } from "src/styles";
import { Spinner } from "react-bootstrap";
import { Pagination } from "src/components/Pagination";
import { Outlet, useNavigate, useParams, useLocation } from "react-router-dom";
import { CleaningLogsTable } from "./CleaningLogsTable";
import { useIsMobile } from "src/hooks/useIsMobile";
import {
  ID,
  LocationSpecificWashroomLogsForTable,
  WashroomsFormInput,
  WashroomsFormInputWithFirebaseKey,
  WashroomsListForTable,
} from "src/types";
import { FirebaseDataService } from "src/services/Firebase/data";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { NumberParam, useQueryParam } from "use-query-params";
import { setOpenedWashroomLogsPage as setPageStore } from "src/redux/actions/openedPages";
import {
  CLIENT_WASHROOMS_TABLE_HEADERS,
  CLIENTS_WASHROOM_LOGS_BREADCRUMBS,
  DEFAULT_PAGE_SIZE,
  INITIAL_LAST_PAGE,
  MANAGER_WASHROOMS_TABLE_HEADERS,
  WASHROOM_LOGS_BREADCRUMBS,
  WASHROOM_LOGS_TABLE_HEADERS,
  WASHROOMS_TABLE_HEADERS_SELECT,
} from "./constants";
import CleaningLogsHeader from "./CleaningLogsHeader";
import WorkRequestListContext from "src/pages/WorkRequests/context";
import { WashroomLogsFilteringOption } from "./CleaningLogsHeader/types";
import { WashroomsTable } from "./WashroomsTable";
import WashroomsTableHeader from "./WashroomsTableHeader";
import WashroomsFormModal from "src/components/organisms/WashroomsFormModal";
import { useModal } from "src/hooks/useModal";
import Modal from "src/components/Modal";
import { WashroomLogsDetails } from "src/pages/WashroomLogsDetails";
import { getUserData } from "src/redux/selectors/user";

const WashroomLogsTabs = () => {
  const { isClient, clientId: clientIdFromStore, firstName, lastName } = useSelector(getUserData);

  const tabs = [
    { eventKey: "1", label: "Cleaning Logs" },
    ...(!isClient ? [{ eventKey: "2", label: "Cleaning Logs Summary" }] : []),
    { eventKey: "3", label: "Washrooms" },
  ];

  const { locationId: locationId, clientCode: clientId } = useParams<{
    clientCode: string;
    locationId: string;
  }>();

  const [activeTab, setActiveTab] = useState("1");

  const { isMobile } = useIsMobile();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [cleaningLogs, setCleaningLogs] = useState<LocationSpecificWashroomLogsForTable[]>([]);
  const [currentCleaningLogsPage, setCurrentCleaningLogsPage] = useState<LocationSpecificWashroomLogsForTable[]>([]);
  const [currentWashroomsPage, setCurrentWashroomsPage] = useState<WashroomsListForTable[]>([]);
  // const { id: employeeId } = useSelector(getUserData);

  const [isLoading, setIsLoading] = useState(false);
  const [exportButtonLoading, setExportButtonLoading] = useState(false);
  const [washroomSelectActive, setWashroomSelectActive] = useState<boolean>(false);
  const [washroomCreateActive, setWashroomCreateActive] = useState<boolean>(false);

  const [openedWashroomLogsPage, setOpenedWashroomLogsPage] = useQueryParam("openedWashroomLogsPage", NumberParam);
  const [lastPage, setLastPage] = useState<number>(INITIAL_LAST_PAGE);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [filteringOptions, setFilteringOptions] = useState<WashroomLogsFilteringOption[]>();
  const [washroomEditActive, setWashroomEditActive] = useState<boolean>(false);
  const [editingWashroom, setEditingWashroom] = useState<WashroomsFormInputWithFirebaseKey | null>(null);

  const [selectedWashrooms, setSelectedWashrooms] = useState<Set<ID>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const { isShowing, toggle } = useModal();

  const routeLocation = useLocation();
  const locationLabel: string = routeLocation.state?.locationLabel || "";

  const pageNum = openedWashroomLogsPage || 1;

  const { category } = useContext(WorkRequestListContext);

  const getPageSize = async () => {
    try {
      const { data } = await FirebaseDataService.getWashroomLogsPageSize();
      setPageSize(data || DEFAULT_PAGE_SIZE);
    } catch (e) {
      setPageSize(DEFAULT_PAGE_SIZE);
    }
  };

  const getRecentCleaningLogs = (logs: LocationSpecificWashroomLogsForTable[]) => {
    const sevenDaysAgo = moment().subtract(90, "days");
    return logs
      .filter((log) => moment(log.cleaningDate).isAfter(sevenDaysAgo)) // only last 7 days
      .sort((a, b) => moment(b.startTime).valueOf() - moment(a.startTime).valueOf()); // newest first
  };

  const handleWashroomSelect = (washroomId: ID, isChecked: boolean) => {
    const newSelectedWashrooms = new Set(selectedWashrooms);

    if (isChecked) {
      newSelectedWashrooms.add(washroomId);
    } else {
      newSelectedWashrooms.delete(washroomId);
    }

    setSelectedWashrooms(newSelectedWashrooms);
  };

  const handleDeleteSelected = async () => {
    if (selectedWashrooms.size === 0) {
      alert("Please select washrooms to delete.");
      return;
    }

    setIsDeleting(true);

    try {
      if (locationId) {
        const washroomIdsToDelete = Array.from(selectedWashrooms);
        const washroomFirebaseKeysToDelete: string[] = [];

        washroomIdsToDelete.forEach((washroomId) => {
          // Find the washroom to get its firebaseKey
          const washroom = currentWashroomsPage.find((w) => w.id === washroomId);
          if (washroom && washroom.firebaseKey) {
            washroomFirebaseKeysToDelete.push(washroom.firebaseKey);
          }
        });

        if (washroomFirebaseKeysToDelete.length === 0) {
          alert("No valid washrooms found to delete.");
          return;
        }

        const response = await FirebaseDataService.deleteWashrooms(locationId, washroomFirebaseKeysToDelete);

        if (response.status === "success") {
          await getWashroomListByLocationId();

          setSelectedWashrooms(new Set());
          setWashroomSelectActive(false);
          toggle();

          console.log(`Successfully deleted ${washroomFirebaseKeysToDelete.length} washroom(s)`);
        } else {
          throw new Error(response.message || "Failed to delete washrooms");
        }
      }
    } catch (error) {
      console.error("Error deleting washrooms:", error);
      alert("An error occurred while deleting washrooms. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCreateWashroom = async (data: WashroomsFormInput) => {
    // setIsLoading(true);
    try {
      if (locationId) {
        const { status } = await FirebaseDataService.createWashroom(locationId, data);
        console.log("Washroom Created!", status);
        setWashroomCreateActive(false);
        getWashroomListByLocationId();
      }
    } catch (error) {
      console.error("Error deleting washrooms:", error);
      alert("An error occurred while deleting washrooms. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const generateWashroomFilteringOptions = (washroomLogs: LocationSpecificWashroomLogsForTable[]) => {
    // Extract unique washroom IDs and labels from the data
    const uniqueWashroomIds = [
      ...new Set(washroomLogs.map((item) => item.washroomId).filter((id) => id !== undefined && id !== null)),
    ];

    const mergedWashroomInfo: { id: string; label: string }[] = uniqueWashroomIds.map((id) => {
      const matchedLabel = currentWashroomsPage.find((washroom) => washroom.id === id)?.label || String(id);
      return { id: String(id), label: matchedLabel };
    });

    // Generate filtering options
    const filteringOptions = mergedWashroomInfo.map((washroom, index) => ({
      value: `Filter By:  ${washroom.label}`,
      id: index + 1,
      filter: [["washroomId", "eq", washroom.id]],
      onlyManager: false,
      washroomId: washroom.id,
    }));

    // Add an "All Washrooms" option at the beginning
    filteringOptions.unshift({
      value: "Filter By:  All",
      id: 0,
      filter: [],
      onlyManager: false,
      washroomId: "",
    });

    return filteringOptions;
  };

  const getWashroomLogs = async () => {
    setIsLoading(true);
    if (!lastPage || (lastPage && pageNum <= lastPage)) {
      try {
        if (locationId) {
          const { data: washroomLogs } = await FirebaseDataService.getWashroomLogsByLocationId(locationId);

          const sortedLogs = getRecentCleaningLogs(washroomLogs);

          const washroomFilteringOptions = generateWashroomFilteringOptions(sortedLogs);

          setFilteringOptions(washroomFilteringOptions);
          // const resultClientsPageArray = sortedLogs.slice((pageNum - 1) * pageSize, pageNum * pageSize);
          const resultClientsPageArray = sortedLogs.slice((pageNum - 1) * pageSize, pageNum * pageSize).map((log) => {
            const matchedWashroom = currentWashroomsPage.find((w) => w.id === log.washroomId);
            return {
              ...log,
              washroomLabel: matchedWashroom?.label || log.washroomId || "N/A",
            };
          });

          setCleaningLogs(sortedLogs);
          setCurrentCleaningLogsPage(resultClientsPageArray);
          setLastPage(Math.ceil(sortedLogs.length / pageSize));
        }
      } catch (error) {
        console.error("Error fetching Washroom logs", error);
      }
    }
    setIsLoading(false);
  };

  const getWashroomListByLocationId = async () => {
    setIsLoading(true);
    if (!lastPage || (lastPage && pageNum <= lastPage)) {
      try {
        if (locationId) {
          const { data: washroomLists } = await FirebaseDataService.getWashroomsByLocationId({ locationId });

          const washroomsArray = Object.entries(washroomLists).map(([key, value]) => ({
            firebaseKey: key,
            ...(typeof value === "object" && value !== null ? value : {}),
          })) as WashroomsListForTable[];

          setCurrentWashroomsPage(washroomsArray);
        }
      } catch (error) {
        setCurrentWashroomsPage([]);
        console.error("Error fetching Washroom logs", error);
      }
    }
    setIsLoading(false);
  };

  const generateNextWashroomDefaults = () => {
    if (currentWashroomsPage.length === 0) {
      return {
        id: "W1",
        label: "Washroom 1",
      };
    }

    // Extract numeric parts from existing washroom IDs
    const existingNumbers = currentWashroomsPage
      .map((washroom) => {
        // Convert ID to string and then apply regex
        const idString = String(washroom.id || "");
        const match = idString.match(/^W(\d+)$/);
        return match ? parseInt(match[1], 10) : 0;
      })
      .filter((num) => num > 0);

    // Find the highest number and increment by 1
    const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;

    return {
      id: `W${nextNumber}`,
      label: `Washroom ${nextNumber}`,
    };
  };

  useEffect(() => {
    getWashroomListByLocationId();
  }, []);

  const onExportButtonClick = async () => {
    setExportButtonLoading(true);
    try {
      const effectiveClientId = isClient ? clientIdFromStore : clientId;
      if (effectiveClientId && locationId) {
        const response = await FirebaseDataService.exportWashroomLogs(effectiveClientId, locationId);
        const { fileData, fileName, mimeType } = response.data;

        // Convert base64 to blob
        const byteCharacters = atob(fileData);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mimeType });

        // Trigger download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        link.click();
        window.URL.revokeObjectURL(url);

        alert("File exported successfully");
      }
    } catch (error) {
      console.error("Error exporting washroom logs report:", error);
      alert("An error occurred while exporting washroom logs. Please try again.");
    } finally {
      setExportButtonLoading(false);
    }
  };

  useEffect(() => {
    if (!washroomSelectActive) {
      setSelectedWashrooms(new Set());
    }
  }, [washroomSelectActive]);

  const onPageChangeClick = (pageNumber: number) => {
    setOpenedWashroomLogsPage(pageNumber);
    dispatch(setPageStore(pageNumber));
    const resultClientsPageArray = cleaningLogs.slice((pageNumber - 1) * pageSize, pageNumber * pageSize).map((log) => {
      const matchedWashroom = currentWashroomsPage.find((w) => w.id === log.washroomId);
      return {
        ...log,
        washroomLabel: matchedWashroom?.label || log.washroomId || "N/A",
      };
    });
    setCurrentCleaningLogsPage(resultClientsPageArray);
  };

  const onRowClick = (washroomId: ID, washroomLabel: string) => {
    const url = `/washroomlogs/${clientId}/${locationId}/${washroomId}/qrcode?washroomLabel=${encodeURIComponent(
      String(washroomLabel || ""),
    )}`;
    navigate(url, { state: { washroomLabel } });
  };

  useEffect(() => {
    setIsLoading(true);
    getPageSize().finally(() => getWashroomLogs());
  }, [pageSize, currentWashroomsPage]);

  const onClickBreadcrumb = (route: ID | null) => {
    if (typeof route === "string") {
      // Ensure all placeholders are replaced before navigation
      let finalRoute = route;

      // Replace any remaining placeholders with actual values
      if (finalRoute.includes(":clientCode") && clientId) {
        finalRoute = finalRoute.replace(":clientCode", clientId);
      }
      if (finalRoute.includes(":locationId") && locationId) {
        finalRoute = finalRoute.replace(":locationId", locationId);
      }

      // Only navigate if no placeholders remain
      if (!finalRoute.includes(":")) {
        navigate(finalRoute);
      } else {
        console.warn("Cannot navigate: route contains unresolved placeholders:", finalRoute);
      }
    }
  };

  const clientBreadcrumbs = CLIENTS_WASHROOM_LOGS_BREADCRUMBS.map((crumb) => {
    const updatedCrumb = { ...crumb };
    if (crumb.id.includes(":clientCode") && clientId) {
      updatedCrumb.id = crumb.id.replace(":clientCode", clientId);
    }
    if (crumb.name === ":clientCode" && clientId) {
      updatedCrumb.name = `${firstName} ${lastName}`;
    }
    if (crumb.id.includes(":locationId") && locationId) {
      updatedCrumb.id = crumb.id.replace(":locationId", locationId);
    }
    if (crumb.name === ":locationId" && locationId) {
      updatedCrumb.name = locationLabel || locationId;
    }
    return updatedCrumb;
  });

  const breadcrumbs = WASHROOM_LOGS_BREADCRUMBS.map((crumb) => {
    const updatedCrumb = { ...crumb };
    if (crumb.id.includes(":clientCode") && clientId) {
      updatedCrumb.id = crumb.id.replace(":clientCode", clientId);
    }
    if (crumb.name === ":clientCode" && clientId) {
      updatedCrumb.name = clientId;
    }
    if (crumb.id.includes("user=client") && clientIdFromStore) {
      updatedCrumb.id = crumb.id.replace("user=client", `user=${clientIdFromStore}`);
    }
    if (crumb.name === ":clientCode" && clientIdFromStore) {
      updatedCrumb.name = `${firstName} ${lastName}`;
    }
    if (crumb.id.includes(":locationId") && locationId) {
      updatedCrumb.id = crumb.id.replace(":locationId", locationId);
    }
    if (crumb.name === ":locationId" && locationId) {
      updatedCrumb.name = locationLabel || locationId;
    }

    return updatedCrumb;
  });

  const onDropDownItemClick = (selectedFilter: any) => {
    let filteredLogs = [...cleaningLogs]; // Create a copy of all logs

    // If it's not the "All Washrooms" option (id === 0), filter by washroomId
    if (selectedFilter.id !== 0 && selectedFilter.washroomId) {
      filteredLogs = cleaningLogs.filter((log) => {
        const logWashroomId = log.washroomId;
        return logWashroomId === selectedFilter.washroomId;
      });
    }

    // Apply pagination to filtered results
    const startIndex = (pageNum - 1) * pageSize;
    const endIndex = pageNum * pageSize;
    const paginatedLogs = filteredLogs.slice(startIndex, endIndex);

    const resultClientsPageArray = paginatedLogs.map((log) => {
      const matchedWashroom = currentWashroomsPage.find((w) => w.id === log.washroomId);
      return {
        ...log,
        washroomLabel: matchedWashroom?.label || log.washroomId || "N/A",
      };
    });

    // Update the state
    setCurrentCleaningLogsPage(resultClientsPageArray);
    setLastPage(Math.ceil(filteredLogs.length / pageSize));

    // Optional: Store the filtered logs and selected filter for future pagination
    // setFilteredLogs(filteredLogs); // You'll need to add this state
    // setSelectedFilter(selectedFilter); // You'll need to add this state
  };

  const handleWashroomEdit = (data: WashroomsFormInput) => {
    const washroomWithFirebaseKey = currentWashroomsPage.find((washroom) => washroom.id === data.id);

    if (!washroomWithFirebaseKey || !washroomWithFirebaseKey.firebaseKey) {
      alert("Error: Could not find washroom firebase key");
      return;
    }
    const editingWashroomData = {
      ...data,
      firebaseKey: washroomWithFirebaseKey.firebaseKey,
    };

    setEditingWashroom(editingWashroomData);
    setWashroomEditActive(true);
  };

  const handleUpdateWashroom = async (formData: WashroomsFormInput) => {
    if (!editingWashroom || !locationId) {
      alert("Error: Missing washroom data or location ID");
      return;
    }

    if (!editingWashroom.firebaseKey) {
      alert("Error: Missing washroom firebase key");
      return;
    }

    setIsLoading(true);
    try {
      const updateData = {
        ...formData,
        // id: editingWashroom.id,
      };

      console.log("Updating washroom with data:", updateData);

      await FirebaseDataService.updateWashroom(locationId, editingWashroom.firebaseKey, updateData);

      setWashroomEditActive(false);
      setEditingWashroom(null);
      await getWashroomListByLocationId();
    } catch (error) {
      console.error("Error updating washroom:", error);
      alert("An error occurred while updating the washroom. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container>
      <TopBar>
        <BreadCrumbs folderStack={isClient ? clientBreadcrumbs : breadcrumbs} onClickItem={onClickBreadcrumb} />
      </TopBar>

      <Content>
        <ContentFrame>
          <HorizontalTabs
            tabs={tabs.map((tab) => ({
              eventKey: tab.eventKey,
              label: tab.label,
              content: activeTab === tab.eventKey ? <></> : null,
            }))}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
          {isLoading ? (
            <EmptyContainer>
              <Spinner animation="border" size="sm" />
            </EmptyContainer>
          ) : (
            <>
              {activeTab === "1" ? (
                <>
                  {currentCleaningLogsPage?.length ? (
                    <>
                      <CleaningLogsHeader
                        defaultItem={
                          filteringOptions?.find((option) => option.washroomId === category) ||
                          filteringOptions?.[0] || {
                            value: "Filter By: All",
                            id: 0,
                            filter: [],
                            onlyManager: false,
                            washroomId: "",
                          }
                        }
                        isLoading={exportButtonLoading}
                        exportButtonHandler={onExportButtonClick}
                        items={
                          filteringOptions?.filter((e: { value: string; id: number }) => e.value || e.id === 0) || []
                        }
                        onDropDownItemClick={onDropDownItemClick}
                      />
                      <CleaningLogsTable
                        onRowClick={() => {}}
                        isLoading={isLoading}
                        headers={WASHROOM_LOGS_TABLE_HEADERS}
                        data={currentCleaningLogsPage}
                        isManager
                      />
                    </>
                  ) : (
                    <NoTicketsMessage $isMobile={isMobile}>No cleaning logs available</NoTicketsMessage>
                  )}
                  <PaginationContainer>
                    <Pagination
                      totalCount={cleaningLogs.length}
                      pageSize={pageSize}
                      onPageChange={onPageChangeClick}
                      currentPage={pageNum}
                    />
                  </PaginationContainer>
                </>
              ) : activeTab === "2" ? (
                <WashroomLogsDetails locationId={locationId ?? ""} clientId={clientId ?? ""} />
              ) : (
                <>
                  {!isClient && (
                    <WashroomsTableHeader
                      washroomSelectActive={washroomSelectActive}
                      setWashroomSelectActive={setWashroomSelectActive}
                      selectedCount={selectedWashrooms.size}
                      onDeleteSelected={handleDeleteSelected}
                      isDeleting={isDeleting}
                      washroomCreateActive={washroomCreateActive}
                      setWashroomCreateActive={setWashroomCreateActive}
                      toggle={toggle}
                    />
                  )}
                  {currentWashroomsPage?.length ? (
                    <>
                      <WashroomsTable
                        onRowClick={onRowClick}
                        isLoading={isLoading}
                        headers={
                          isClient
                            ? CLIENT_WASHROOMS_TABLE_HEADERS
                            : washroomSelectActive
                              ? WASHROOMS_TABLE_HEADERS_SELECT
                              : MANAGER_WASHROOMS_TABLE_HEADERS
                        }
                        data={currentWashroomsPage}
                        washroomSelectActive={washroomSelectActive}
                        selectedWashrooms={selectedWashrooms}
                        onWashroomSelect={handleWashroomSelect}
                        onWashroomEdit={handleWashroomEdit}
                        isClient={isClient}
                      />
                      <PaginationContainer>
                        <Pagination
                          totalCount={currentWashroomsPage.length}
                          pageSize={pageSize}
                          onPageChange={onPageChangeClick}
                          currentPage={pageNum}
                        />
                      </PaginationContainer>
                    </>
                  ) : (
                    <NoTicketsMessage $isMobile={isMobile}>No washrooms added</NoTicketsMessage>
                  )}
                </>
              )}
            </>
          )}
        </ContentFrame>
      </Content>
      <Modal
        onApproveClick={handleDeleteSelected}
        onCancelClick={() => toggle()}
        title="Delete Selected Washroom(s)"
        textContent="Are you sure you want to delete selected Washroom(s)? All entered information will be lost."
        isShowing={isShowing}
        hide={toggle}
        approveButtonText="Delete"
        approveButtonStyle="decline"
      />
      {(washroomCreateActive || washroomEditActive) && (
        <WashroomsFormModal
          closeModal={() => {
            setWashroomCreateActive(false);
            setWashroomEditActive(false);
            setEditingWashroom(null);
          }}
          onSubmit={washroomEditActive ? handleUpdateWashroom : handleCreateWashroom}
          defaultValues={
            washroomEditActive && editingWashroom
              ? {
                  id: editingWashroom.id,
                  label: editingWashroom.label || `Washroom ${editingWashroom.id}`,
                }
              : generateNextWashroomDefaults()
          }
          error=""
          isEditMode={washroomEditActive}
        />
      )}
      <Outlet />
    </Container>
  );
};

export default WashroomLogsTabs;
